import { waitForSnowplowIds } from '@/services/snowplow'
import { EMAIL_CLICK_IDENTIFY_SOURCE, IDENTIFY_EVENT_NAME } from '@/services/wapi'

/** Appended by Blueshift to every tracked link. Only its presence is ever used. */
const BSFT_UID_PARAM = 'bsft_uid'

/**
 * Separate from the reporting call so the caller can guard before doing any work —
 * almost no page load is email-driven, and those should cost one query-string read.
 */
export function isEmailDrivenSession(search: string): boolean {
    return Boolean(new URLSearchParams(search).get(BSFT_UID_PARAM))
}

/** `no-cookie` is an ordinary result, not a failure — hence an outcome, not a throw. */
export type EmailClickIdentifyOutcome = 'sent' | 'no-cookie' | 'rejected' | 'network-error'

/**
 * Reports an email-driven session so Blueshift can stitch the anonymous Snowplow cookie
 * to the recipient of the clicked email. See wanderu/strategy#418.
 *
 * Assumes the caller has established this *is* an email-driven session via
 * `isEmailDrivenSession` — it does not re-check.
 */
export async function reportEmailClickIdentify({
    locale,
}: {
    locale: string
}): Promise<EmailClickIdentifyOutcome> {
    const snowplowIds = await waitForSnowplowIds()

    if (!snowplowIds?.sp_domain_id) {
        // sp_domain_id becomes Blueshift's `cookie`; without it there is nothing to
        // stitch, so sending would only create an unattached anonymous profile.
        return 'no-cookie'
    }

    try {
        const response = await fetch('/api/nexus/user-events', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                event: IDENTIFY_EVENT_NAME,
                source: EMAIL_CLICK_IDENTIFY_SOURCE,
                locale,
                snowplowIds,
            }),
            // Survives page unload — a visitor who navigates straight off the landing is
            // exactly the one worth identifying.
            keepalive: true,
        })

        return response.ok ? 'sent' : 'rejected'
    } catch {
        return 'network-error'
    }
}
